import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      // Small delay to ensure auth state is properly set
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get the session from the URL
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error during auth callback:', error);
        navigate('/auth');
        return;
      }

      if (session) {
        // Wait a bit more to ensure profile trigger has executed
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Track affiliate referral - check storage AND user metadata
        const affiliateRef = 
          localStorage.getItem('affiliate_ref') || 
          sessionStorage.getItem('affiliate_ref') ||
          session.user.user_metadata?.affiliate_ref;
          
        if (affiliateRef) {
          try {
            // Look up the affiliate link by code
            const { data: affiliateLink } = await supabase
              .from('affiliate_links')
              .select('id, active')
              .eq('code', affiliateRef)
              .single();
            
            if (affiliateLink && affiliateLink.active) {
              // Check if this user was already referred (prevent duplicates)
              const { data: existingReferral } = await supabase
                .from('affiliate_referrals')
                .select('id')
                .eq('referred_user_id', session.user.id)
                .maybeSingle();
              
              if (!existingReferral) {
                // Create the referral record
                const { error: insertError } = await supabase
                  .from('affiliate_referrals')
                  .insert({
                    affiliate_link_id: affiliateLink.id,
                    referred_user_id: session.user.id,
                  });
                  
                if (insertError) {
                  console.error('Error inserting referral:', insertError);
                } else {
                  console.log('Affiliate referral tracked successfully for code:', affiliateRef);
                }
              }
            }
          } catch (err) {
            console.error('Error tracking affiliate referral:', err);
          } finally {
            // Always clear the ref from storage
            localStorage.removeItem('affiliate_ref');
            sessionStorage.removeItem('affiliate_ref');
          }
        }
        
        // Get user profile and role securely
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', session.user.id)
          .single();
        
        const { data: userRole } = await supabase
          .rpc('get_user_role', { _user_id: session.user.id });
        
        // SECURITY FIX: Use sessionStorage instead of localStorage for temporary order data
        const pendingOrderId = sessionStorage.getItem('pending_order_id');
        
        if (pendingOrderId) {
          // User came from product checkout, go to payment success to complete delivery info
          // Clear the session storage after reading to prevent stale data
          const orderId = pendingOrderId;
          sessionStorage.removeItem('pending_order_id');
          sessionStorage.removeItem('pending_tracking_number');
          sessionStorage.removeItem('pending_reference');
          navigate(`/payment-success?orderId=${orderId}`);
          return;
        }
        
        // Check if this is a new user who needs to select a role
        if (!userRole || (userRole === 'buyer' && !profile?.onboarding_completed)) {
          navigate('/role-selection');
          return;
        }
        
        // Redirect based on role and onboarding status
        if (userRole === 'seller') {
          navigate('/seller/dashboard');
        } else if (userRole === 'admin') {
          navigate('/admin');
        } else {
          // For buyers: new users go to products for onboarding, existing users go to orders
          if (profile?.onboarding_completed) {
            navigate('/buyer/orders');
          } else {
            navigate('/products');
          }
        }
      } else {
        navigate('/auth');
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}